import React, { useEffect, useContext, useRef, useState } from 'react'
import cl from 'classnames'
import { default as ProfileIcon } from '@assets/icons/placeholder-profile.svg?react'
import { default as ChatIcon } from '@assets/icons/ew-chat.svg?react'
import { default as CartIcon } from '@assets/icons/vwp/CartIcon.svg?react'
import { useListenKey } from '@hooks/index'
import ThemeContext from '@contexts/themeContext'
import ClientOnly from '../../ClientOnly'
import { getImageUrl } from '@helper/getImageUrl'

import brandStyles from './../_brand.module.scss'
import scss from './NavButton.module.scss'

import { Dropdown } from 'react-bootstrap';
import LoginContext from '@contexts/loginContext';

import type { ProfileDropdownMenuProps } from './Dropdown'
import { redirectToInbox } from '@helper/urlHelpers'
import { getCountry, getCurrencySymbol, getMessagingKey } from '@helper/getConfigValues'

import SBBadge from '@sendbird/uikit-react/ui/Badge';
import Checkbox from "@ui/Checkbox";
import Loading from "@ui/Loading";
import { getCookieByName } from '@helper/cookies'
import { CART_COOKIE_COUNT, PACKAGES_DEPOSIT } from '@helper/constants'
import { getVwpCart, VwpCart } from '@api/getVwpCart'
import { toggleRedeemPoints } from '@api/toggleRedeemPoints'
import useUser from '@hooks/useUser'
import { MessagingUnreadCountProps } from '@components/Messaging/MessagingUnreadCount'
import { MessagingDrawerProps } from '@components/Messaging/MessagingDrawer'

const MessagingCount = React.lazy(() => import('@components/MessagingCount'))
const MessagingDrawer = React.lazy(() => import('@components/MessagingDrawer'))
const ProfileDropdownMenu = React.lazy(() => import('../ProfileDropdownMenu'))

interface ProfileNavButtonProps {
  'aria-expanded': boolean,
  isLoggedIn: boolean,
  userIcon: string,
  onClick: () => void,
}

const ProfileNavButton = React.forwardRef((
  { onClick, isLoggedIn, userIcon, ...rest }: ProfileNavButtonProps,
  ref
) => {
  useListenKey(ref as React.RefObject<HTMLDivElement>, onClick, 'Enter')
  const isDark = useContext(ThemeContext) === 'dark'
  const menuIconColour = isDark ? brandStyles.space : brandStyles.shade;

  return (<div
    ref={ref as React.RefObject<HTMLDivElement>}
    tabIndex={0}
    className={scss.button}
    onClick={onClick}
    data-testid="nav-profile-trigger"
  >
    {isLoggedIn && userIcon ?
      <img className={scss.profileImg} src={getImageUrl(userIcon, 30, 30, false)} />
      : <ProfileIcon className={cl(scss.profileIcon, 'ew-avatar', !isLoggedIn && scss.profileIconLoggedOut)} stroke={menuIconColour} />}
  </div>)
})

interface CartNavButtonProps {
  'aria-expanded': boolean,
  count: number,
  onClick: () => void,
}

const CartNavButton = React.forwardRef((
  { count, onClick }: CartNavButtonProps,
  ref
) => {
  useListenKey(ref as React.RefObject<HTMLDivElement>, onClick, 'Enter')
  const isDark = useContext(ThemeContext) === 'dark';

  return (<div
    ref={ref as React.RefObject<HTMLDivElement>}
    tabIndex={0}
    className={scss.dropDownCart}
    onClick={onClick}
    data-testid="nav-cart-trigger"
  >
    <div className={scss.cartIconContainer}>
      <CartIcon className={isDark ? scss.cartIconLight : scss.cartIconDark} />
      {count > 0 && <div className={scss.shoppingCount}>
        <SBBadge className={isDark ? scss.shoppingCountBadgeLight : scss.shoppingCountBadgeDark} count={count}></SBBadge>
      </div>}
    </div>
  </div>)
})

interface CartInfoItemProps {
  image: string,
  supplier: string,
  title: string,
  price: number,
}

const CartInfoItem = ({ image, supplier, title, price }: CartInfoItemProps) => {
  return (<div className={scss.cartItem}>
    <div className={scss.cartItemImg}>
      <img src={image} />
    </div>
    <div className={scss.cartItemInfo}>
      <div className={`${scss.cartItemSupplier} ${scss.textElipsis}`}>{supplier}</div>
      <div className={`${scss.cartItemTitle} ${scss.textElipsis}`}>{title}</div>
      <div className={`${scss.cartItemPrice} ${scss.textElipsis}`}>{getCurrencySymbol()}{price.toLocaleString()}</div>
    </div>
  </div>);
}

interface CartInfoProps {
  cartCount: number;
}

const CartInfo = ({ cartCount }: CartInfoProps) => {
  const [useWedPoints, setUseWedPoints] = useState<boolean>(true);
  const [cart, setCart] = useState<VwpCart | null>(null);
  const [loadingCart, setLoadingCart] = useState<boolean>(false);

  const currency = getCurrencySymbol();

  const updateRedeemPoints = () => {
    setUseWedPoints(prev => {
      const redeem = !prev;
      toggleRedeemPoints(redeem);
      return redeem;
    });
  };

  const getMaxRedeemAmount = (userCart: VwpCart | null) => {
    return !!userCart && userCart.userWedPoints ?
      parseFloat((userCart.userWedPoints / userCart.wedPointsRedeemRate > (userCart.subTotal - PACKAGES_DEPOSIT) ?
        (userCart.subTotal - PACKAGES_DEPOSIT) :
        userCart.userWedPoints / userCart.wedPointsRedeemRate).toFixed(2)
      ) : 0;
  };

  useEffect(() => {
    const loadItems = async () => {
      setLoadingCart(true);

      try {
        const result = await getVwpCart();
        setCart(result);

        if (!!result) {
          setUseWedPoints(result.redeemPoints);
        }
      } catch (error) {
        console.error('Error loading cart items', error);
      } finally {
        setLoadingCart(false);
      }
    };

    (cartCount > 0) && loadItems();
  }, [cartCount]);

  if (loadingCart) {
    return (<div className={'d-flex justify-content-center align-items-center w-100'}>
      <Loading />
    </div>);
  }

  return (<>
    {!cart || cart.categories.length === 0 ?
      <div className={scss.emptyCart}>
        <p>Your cart is empty</p>
        <a className={`btn btn-large btn-inverse w-100 mb-3 ${scss.browseButton}`}
          href={'/packages/'}
        >
          Browse 100+ Supplier Packages
          <i className={'fa-solid fa-long-arrow-right ms-1 primary'}></i>
        </a>
      </div> :
      <div>
        {cart.categories.map((category, index) => {
          const { packageItem } = category;
          let price = packageItem.rrpPrice;

          category.extras.forEach(extra => {
            const extraItem = packageItem.extras.find(e => e.id === extra.extraId);
            if (!!extraItem) {
              price += extraItem.rrpPrice * extra.quantity;
            }
          });

          return (
            <CartInfoItem
              key={index}
              image={packageItem.supplierListingImage}
              supplier={packageItem.supplierName}
              title={packageItem.name}
              price={price}
            />
          );
        })}
        {(!!cart.userWedPoints && cart.userWedPoints > 0) && <div className={scss.wedPointsCheck}>
          <Checkbox
            className={scss.checkBox}
            isChecked={useWedPoints}
            onClick={updateRedeemPoints}>
            <span>Use your WedPoints to save {currency}{getMaxRedeemAmount(cart).toLocaleString()}</span>
          </Checkbox>
        </div>}
      </div>
    }
    <div className={scss.priceContainer}>
      <div className={scss.totalText}>TOTAL</div>
      <div className={scss.totalPrice}>
        {currency}{!!cart ? (cart.subTotal - (useWedPoints ? getMaxRedeemAmount(cart) : 0)).toLocaleString() : '0.00'}
      </div>
    </div>
    <div className={scss.buttons}>
      <a href={'/packages/checkout'} className={`btn btn-large btn-inverse w-100 ${scss.checkoutButton}`}>CHECKOUT</a>
      <a href={'/packages/cart'} className={`btn ${scss.viewCart}`}><CartIcon /> VIEW CART</a>
    </div>
  </>);
}

interface ProfileNavigationContainerProps {
  isCompetitionEnabled: boolean
  messagingEnabled: boolean
  iconUrl?: string
  editUserAvatar?: React.ReactNode
}


function ProfileNavigationContainer({ isCompetitionEnabled, messagingEnabled, iconUrl = '', editUserAvatar = undefined }: ProfileNavigationContainerProps) {
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);

  const [unreadCount, setUnreadCount] = useState<number>(1);
  const [cartCount, setCartCount] = useState<number>(0);
  const [cartOpen, setCartOpen] = useState<boolean>(false);

  const [showConciergePref, setShowConciergePref] = useState<boolean>(false);

  const { isLoggedIn, setIsLoggedIn } = useContext(LoginContext);
  const fetchLoggedInUserRef = useRef<() => void>();

  const { user: {icon: userIcon, name: userName, id: userId, isConciergeChannelCreated} } = useUser(isLoggedIn, { ProfileImage: iconUrl });

  const fetchLoggedInUser = () => {
    setIsLoggedIn(true);
  };

  fetchLoggedInUserRef.current = fetchLoggedInUser;
  const messagingKey = getMessagingKey();

  const closeParentDropdown = () => {
    setDropdownOpen(false);
  };

  const handleToggle = (nextShow: boolean) => {
    setDropdownOpen(nextShow);
  };

  const handleCartToggle = (nextShow: boolean) => {
    setCartOpen(nextShow);
  };

  const handleInboxClick = () => {
    if (typeof window === 'undefined') {
      return;
    }

    if (isConciergeChannelCreated) {
      redirectToInbox();
    }
    else if (messagingEnabled && !!userId && !!messagingKey) {
      setShowConciergePref(true);
    }
  };

  const handleClose = () => {
    setShowConciergePref(false);
  };

  useEffect(() => {
    (window as any).fetchLoggedInUserRef = fetchLoggedInUserRef;
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const cookieCount = getCookieByName(CART_COOKIE_COUNT);
    if (!!cookieCount) {
      setCartCount(parseInt(cookieCount));
    }

    const handleAddToCart = () => {
      setCartCount(prev => prev + 1);
      setCartOpen(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    window.addEventListener('incrementCartCount', handleAddToCart);

    return () => {
      window.removeEventListener('incrementCartCount', handleAddToCart);
    };
  }, []);

  const MessagingConciergePreferencesModal = React.lazy(() => import('@components/Messaging/MessagingConciergePreferencesModal'));
  const isDark = useContext(ThemeContext) === 'dark';
  const countryCode = getCountry();

  return (<>
      <div className={scss.profileContainer}>
        {(messagingEnabled && !!userId && !!messagingKey) ?
          <div className={scss.inboxIconContainer} onClick={handleInboxClick}>
            <ChatIcon className={isDark ? scss.inboxIconLight : scss.inboxIconDark} />
              <div className={scss.unreadCount}>
                {isConciergeChannelCreated ?
                  <ClientOnly<MessagingUnreadCountProps>
                    Component={MessagingCount}
                    appKey={messagingKey}
                    userId={userId}
                    className={scss.unreadCountBadge}
                    onUnreadCountChange={setUnreadCount}
                  /> :
                  <SBBadge className={scss.unreadCountBadge} count={unreadCount}></SBBadge>}
              </div>
          </div> :
          <div className={`${scss.inboxIconContainer} ${scss.inactive}`}></div>
        }
        {countryCode !== 'uk' && <Dropdown className={scss.cartToggle} show={cartOpen} onToggle={handleCartToggle}>
          <Dropdown.Toggle
            as={CartNavButton}
            variant={'success'}
            count={cartCount}
          />
          <Dropdown.Menu align={'end'} className={scss.cartItemsContainer}>
            <CartInfo cartCount={cartCount} />
          </Dropdown.Menu>
        </Dropdown>}
        <Dropdown className={scss.buttonContainer + ' ' + (isDark ? scss.dark : '')} show={dropdownOpen} onToggle={handleToggle}>
          <Dropdown.Toggle as={ProfileNavButton} variant="success" id="dropdown-basic"
            isLoggedIn={isLoggedIn}
            userIcon={iconUrl === '' ? userIcon : iconUrl}
          >
          </Dropdown.Toggle>
          <Dropdown.Menu align="end" className={`${scss.dropdown} ${scss.profileDropdown}`}>
            {!!ProfileDropdownMenu && <ClientOnly<ProfileDropdownMenuProps>
              Component={ProfileDropdownMenu}
              isCompetitionEnabled={isCompetitionEnabled}
              isLoggedIn={isLoggedIn}
              userName={userName}
              editUserAvatar={editUserAvatar}
              closeParentDropdown={closeParentDropdown}
            />}
          </Dropdown.Menu>
        </Dropdown >
      </div>
      {showConciergePref && <ClientOnly Component={MessagingConciergePreferencesModal} handleClose={handleClose} />}
    {messagingEnabled && <ClientOnly<MessagingDrawerProps>
      Component={MessagingDrawer}
      isLoggedIn={isLoggedIn}
      userId={userId}
      unreadCount={unreadCount}
    />}
  </>)
}

export default ProfileNavigationContainer